import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { accessibleNavItems, navCategories, devNav, type NavItem } from '@/mocks/nav';
import { selectAdminModuleSlugs, selectIsOwner } from '@/redux/auth/selectors';
import { useAppSelector } from '@/store/hooks';
import { DEV_ONLY } from '@/config/config';
import { Icon } from '@/components/common';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar = ({ collapsed, onToggleCollapse }: SidebarProps) => {
  const { t } = useTranslation();
  const [openSecs, setOpenSecs] = useState<Record<string, boolean>>({});

  // Nav is gated by the user's accessible modules (from the select-company response).
  const adminSlugs = useAppSelector(selectAdminModuleSlugs);
  const isOwner = useAppSelector(selectIsOwner);
  const items = accessibleNavItems(adminSlugs, isOwner);

  // Standalone items (no category) render above the grouped sections.
  const standalone = items.filter((item) => !item.category);

  const renderItem = (item: NavItem) => (
    <li key={item.key}>
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `app-sidebar__link${isActive ? ' app-sidebar__link--active' : ''}`
        }
        title={collapsed ? t(item.labelKey) : undefined}
      >
        <span className='app-sidebar__link-indicator' aria-hidden='true' />
        <span className='app-sidebar__link-icon'>
          <Icon name={item.icon} size={18} />
        </span>
        {!collapsed && <span className='app-sidebar__link-text'>{t(item.labelKey)}</span>}
      </NavLink>
    </li>
  );

  const renderGroup = (key: string, labelKey: string, items: NavItem[]) => {
    if (items.length === 0) return null;
    const open = openSecs[key] !== false;
    return (
      <div className='app-sidebar__group' key={key}>
        {collapsed ? (
          <span className='app-sidebar__group-divider' aria-hidden='true' />
        ) : (
          <button
            type='button'
            className='app-sidebar__group-header'
            aria-expanded={open}
            onClick={() => setOpenSecs((s) => ({ ...s, [key]: !open }))}
          >
            <span className='app-sidebar__group-label'>{t(labelKey)}</span>
            <span className={`app-sidebar__group-chevron${open ? '' : ' is-collapsed'}`}>
              <Icon name='chevron-down' size={14} />
            </span>
          </button>
        )}
        {(collapsed || open) && <ul className='app-sidebar__list'>{items.map(renderItem)}</ul>}
      </div>
    );
  };

  return (
    <aside className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}>
      {/* Nav */}
      <nav className='app-sidebar__nav'>
        <ul className='app-sidebar__list'>{standalone.map(renderItem)}</ul>
        {navCategories.map((cat) =>
          renderGroup(
            cat.key,
            cat.labelKey,
            items.filter((item) => item.category === cat.key),
          ),
        )}
        {DEV_ONLY && renderGroup('dev', 'sidebar_group_dev', devNav)}
      </nav>

      {/* Collapse toggle */}
      <button
        type='button'
        className='app-sidebar__collapse'
        onClick={onToggleCollapse}
        aria-label={t('header_collapse_sidebar')}
        title={t('header_collapse_sidebar')}
      >
        <span className={`app-sidebar__collapse-chevron${collapsed ? ' is-collapsed' : ''}`} />
      </button>
    </aside>
  );
};

export default Sidebar;
